package com.starparty.app.astro0

import com.starparty.app.astro0.celestialobjects.Orrery
import com.starparty.app.geometry.SP_Point
import com.starparty.app.vector_maths.Point3d
import kotlin.math.PI

// import java.awt.*;
// import java.io.*;
// import java.awt.event.*;
/**
 * Title:        Starparty Astro-0
 * Description:  Simply astronomy package for starparty.com and sennen-cove-cornwall.uk
 * Copyright:    Copyright (c) 2021
 * Company: Sparparty.com
 * @author Rob Leeson
 * @version 2.0
 */
class Sky( /*InputStream starsIS,
             InputStream constellationsIS,*/
    time: SP_Time,
    place: SP_Place

) {
    companion object
    {
        val DEFAULT_ALT = 0.0 // 47.0
        val DEFAULT_AZM = 0.0

        val NO_GRID = 0
        val ZERO_GRID = 1
        val BOTH_GRIDS = 2
        val FULL_GRID = 3

        val NO_TITLES = 0
        val FULL_TITLES = 1
        val ABBR_TITLES = 2

        val NO_CONSTELLATIONS = 0
        val CONSTELLATION_LINES_ONLY = 1
        val CONSTELLATION_LINES_AND_BOUNDARIES = 2
        val CONSTELLATION_BOUNDARIES_ONLY = 3
    }

    var showLabels: Int = FULL_TITLES

    protected var itsEquGridType = ZERO_GRID
    protected var itsAltAzmGridType = BOTH_GRIDS

    var deepSkyField: DeepSkyField
        protected set
    var starField: StarField
        protected set
    var orrery: Orrery
        protected set
    var coordinateTransformer: CoordinateTransformer
        protected set
    var equGrid = Grid(12, 11)
        protected set
    var equZeroGrid =
        Grid(2, 1)
        protected set
    var altAzmGrid =
        Grid(12, 11)
        protected set
    var altAzmZeroGrid =
        Grid(2, 1)
        protected set

    fun centreOnEquatorialCoordinates(equ: SphPoint) {
        coordinateTransformer.setEquatorialCoordinatesOfCenter(equ.rA, equ.dec)
    }

    fun centreHorizonOnEquatorialCoordinatesZenithUp(equ: SphPoint) {
        coordinateTransformer.setEquatorialCoordinatesToMiddleWithZenithUp(equ.rA, equ.dec)
    }

    fun centreOnConstellation(index: Int) {
        val con = starField.constellations[index]
        val equ = con.titlePoint.equatorialCoords
        coordinateTransformer.setEquatorialCoordinatesOfCenter(equ.rA, equ.dec)
    }

    fun centreOnPlanet(index: Int) {
        val planet = orrery.bodies[index]
        val equ = planet.equatorialCoords
        coordinateTransformer.setEquatorialCoordinatesOfCenter(equ.rA, equ.dec)
    }

    var tracking: Boolean
        get() = coordinateTransformer.tracking
        set(track) {
            coordinateTransformer.tracking = track
        }

    fun getTime(): SP_Time {
        return coordinateTransformer.getTime()
    }

    fun setTime(t: SP_Time) {
        coordinateTransformer.setTime(t)
     //   deepSkyField.setTime(t)
    //    starField.setTime(t)
        orrery.setTime(t)
    }

    fun getPlace(): SP_Place {
        return coordinateTransformer.getPlace()
    }

    fun setLat(lat: Double) {
        coordinateTransformer.setLat(lat)
    }

    fun setLon(lon: Double) {
        coordinateTransformer.setLon(lon)
    }

    /** Sets the specified equatorial coordinates (in decimal radians)
     * to the current center point of the screen.  */
    fun setEquatorialCoordinates(ra: Double, dec: Double) {
        coordinateTransformer.setEquatorialCoordinatesToMiddle(ra, dec)
        val center = centerPoint
        coordinateTransformer.moveEquatorialPointToPoint(ra, dec, center)
    }


    /** Sets the specified horizontal coordinates (in decimal radians)
     * to the current center point of the screen and the zenith to the up direction  */
    fun setAltAzmCoordinatesWithZenithUp(azm: Double, alt: Double) {
        coordinateTransformer.setAltAzmCoordinatesToMiddleWithZenithUp(azm, alt)
    }

    /** Sets the specified horizontal coordinates (in decimal radians)
     * to the current center point of the screen.  */
    fun setAltAzmCoordinates(azm: Double, alt: Double) {
        coordinateTransformer.setAltAzmCoordinatesToMiddle(azm, alt)
        val center = centerPoint
        val equ = coordinateTransformer.altAzmSphericalToEquatorialSpherical(azm, alt)
        coordinateTransformer.moveEquatorialPointToPoint(equ.rA, equ.dec, center)
    }

    var centerPoint: SP_Point
        get() = coordinateTransformer.centerPoint
        set(center) {
            coordinateTransformer.centerPoint = center
        }

    val zenith: SP_Point
        get() = coordinateTransformer.projectedZenith

    val nadir: SP_Point
        get() = coordinateTransformer.projectedNadir

    val north: SP_Point
        get() = coordinateTransformer.projectedNorth

    val south: SP_Point
        get() = coordinateTransformer.projectedSouth

    val east: SP_Point
        get() = coordinateTransformer.projectedEast

    val west: SP_Point
        get() = coordinateTransformer.projectedWest


    fun rotateEquatorialGridType() {
        var gridType = showEquGridType
        gridType++
        if (gridType > FULL_GRID) { gridType = 0 }
        showEquGridType = gridType
    }

    fun rotateAltAzmGridType() {
        var gridType = showAltAzmGridType
        gridType++
        if (gridType > FULL_GRID) { gridType = 0 }
        showAltAzmGridType = gridType
    }

    fun rotateConstellationType() {
        var constellationType = starField.showConstellationType
        constellationType++
        if (constellationType > CONSTELLATION_BOUNDARIES_ONLY) { constellationType = 0 }
        starField.showConstellationType = constellationType
    }

    var showEquGridType: Int
        get() = itsEquGridType
        set(gridType) {
            itsEquGridType = gridType
            projectEquGrid()
        }

    var showAltAzmGridType: Int
        get() = itsAltAzmGridType
        set(gridType) {
            itsAltAzmGridType = gridType
            projectAltAzmGrid()
        }

    fun movePointToPoint(from: SP_Point, to: SP_Point) {
        val equFrom = coordinateTransformer.getEquatorialCoordinates(from)
        val equTo = coordinateTransformer.getEquatorialCoordinates(to)
        val aaFrom = coordinateTransformer.EquSphericalToAltAzmSpherical(equFrom.rA, equFrom.dec)
        val aaTo = coordinateTransformer.EquSphericalToAltAzmSpherical(equTo.rA, equTo.dec)

        val altDif = aaFrom.alt - aaTo.alt
        val azmDif = aaFrom.azm - aaTo.azm

        val efdec = equFrom.dec / MathD.DEG_TO_RAD
        val efRA = equFrom.rA / MathD.DEG_TO_RAD
        val afAlt = aaFrom.alt / MathD.DEG_TO_RAD
        val afAzm = aaFrom.azm / MathD.DEG_TO_RAD

        val etdec = equTo.dec / MathD.DEG_TO_RAD
        val etRA = equTo.rA / MathD.DEG_TO_RAD
        val atAlt = aaTo.alt / MathD.DEG_TO_RAD
        val atAzm = aaTo.azm / MathD.DEG_TO_RAD

        console.log("movePointToPoint fromX:${from.x} fromY:${from.y}")
        console.log("movePointToPoint toX:${to.x} toY:${to.y}")
        console.log("movePointToPoint fromDec:$efdec fromRA:$efRA")
        console.log("movePointToPoint toDec:$etdec toRA:$etRA")
        console.log("movePointToPoint fromAlt:$afAlt fromAzm:$afAzm")
        console.log("movePointToPoint toAlt:$atAlt toAzm:$atAzm")
        val zenith = coordinateTransformer.getProjectedAltAzimuthPoint((PI /2.0), 0.0)

        console.log("altDif:$altDif")
        if (to.y < zenith.y){
            incrementAlt(-altDif)
        } else {
            incrementAlt(altDif)
        }

        incrementAzm(-azmDif)
    }

    fun movePointToPoint2(from: SP_Point, to: SP_Point) {
        coordinateTransformer.movePointToPoint(from, to)
    }

    fun EquToAltAzm(ra: Double, dec: Double): SphPoint {
        return coordinateTransformer.EquSphericalToAltAzmSpherical(ra, dec)
    }

    val centerPointAltAzimuthCoordinates: SphPoint
        get() = coordinateTransformer.altAzmSphericalCoordsOfCenterPoint

    val centerPointEquatorialCoordinates: SphPoint
        get() = coordinateTransformer.equSphericalCoordsOfCenterPoint

    fun getEquatorialCoordinates(p: SP_Point): SphPoint {
        return coordinateTransformer.getEquatorialCoordinates(p)
    }


    var zoomFactor: Double
        get() = coordinateTransformer.projection.zoomFactor
        set(zoomFactor) {
            coordinateTransformer.projection.zoomFactor = zoomFactor
        }

    fun incrementZoomFromEquPoint(inc: Double, equ: SphPoint) {
     //   coordinateTransformer.incrementZoomFromEquPoint(inc, equ)
    }

    fun incrementZoom(inc: Double) {
        coordinateTransformer.incrementZoomFromCenter(inc)
    }

    fun panHorizontal(radians: Double) {
        coordinateTransformer.panHorizontal(radians)
    }

    fun panVertical(radians: Double) {
        coordinateTransformer.panVertical(radians)
    }

    fun roll(radians: Double) {
        coordinateTransformer.rollAroundCenter(radians)
    }

    fun incrementRA(radians: Double) {
        coordinateTransformer.incrementRA(radians)
    }

    fun incrementDec(radians: Double) {
        coordinateTransformer.incrementDecAroundCenter(radians)
    }

    fun incrementAlt(radians: Double) {
        coordinateTransformer.incrementAltAroundCenter(radians)
    }

    fun incrementAzm(radians: Double) {
        coordinateTransformer.incrementAzm(radians)
    }

    fun project() {
        val equTrans = coordinateTransformer.equatorialTransform
        deepSkyField.project(equTrans)
        starField.project(equTrans)
        orrery.project(equTrans)
        projectEquGrid()
        projectAltAzmGrid()
    }

    fun projectEquGrid() {
        if (itsEquGridType == FULL_GRID || itsEquGridType == BOTH_GRIDS) {
            projectEquFullGrid()
        }
        if (itsEquGridType == ZERO_GRID || itsEquGridType == BOTH_GRIDS) {
            projectEquZeroGrid()
        }
    }

    fun projectAltAzmGrid() {
        if (itsAltAzmGridType == FULL_GRID || itsAltAzmGridType == BOTH_GRIDS) {
            projectAltAzmFullGrid()
        }
        if (itsAltAzmGridType == ZERO_GRID || itsAltAzmGridType == BOTH_GRIDS) {
            projectAltAzmZeroGrid()
        }
    }

    protected fun projectEquFullGrid() {
        val trans = coordinateTransformer.equatorialTransform
        equGrid.project(trans)
    }

    protected fun projectAltAzmFullGrid() {
        val trans = coordinateTransformer.altAzmTransform
        altAzmGrid.project(trans)
    }

    protected fun projectEquZeroGrid() {
        val trans = coordinateTransformer.equatorialTransform
        equZeroGrid.project(trans)
    }

    protected fun projectAltAzmZeroGrid() {
        val trans = coordinateTransformer.altAzmTransform
        altAzmZeroGrid.project(trans)
    }

    fun setupInitialView(){
        setAltAzmCoordinatesWithZenithUp(DEFAULT_AZM, DEFAULT_ALT)

       // setAltAzmCoordinatesWithZenithUp(DEFAULT_AZM, DEFAULT_ALT - 5.0)
    }

    init {
        coordinateTransformer = CoordinateTransformer(time, place)
        val t = getTime()

        deepSkyField = DeepSkyField(t)
        starField = StarField(t /*, starsIS, constellationsIS*/)
        orrery = Orrery(t)
        showEquGridType = itsEquGridType
        showAltAzmGridType = itsAltAzmGridType

        setupInitialView()

    }
}